import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api from '../api';
import { getSocket } from '../socket';
import MenuCard from '../components/MenuCard';
import CategoryTabs from '../components/CategoryTabs';
import SeniorModeToggle from '../components/SeniorModeToggle';
import CartDrawer from '../components/CartDrawer';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import Toast, { useToast } from '../components/Toast';
import StaffCallSheet from '../components/StaffCallSheet';

// Bottom sheet for menu item detail + add to cart
function MenuDetailSheet({ menu, onClose, onAddToCart }) {
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});

  if (!menu) return null;

  const isSoldOut = menu.soldOut || menu.isSoldOut || menu.is_sold_out === 1 || menu.is_sold_out === true || menu.status === 'soldout';
  const options = menu.options || [];

  const optionsExtra = options.reduce((sum, opt) => {
    const key = opt.id || opt.name;
    return sum + (selectedOptions[key] ? (opt.price_delta || 0) : 0);
  }, 0);

  const totalPrice = (menu.price + optionsExtra) * quantity;

  const toggleOption = (opt) => {
    const key = opt.id || opt.name;
    setSelectedOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAdd = () => {
    if (isSoldOut) return;
    const selectedList = options.filter((opt) => selectedOptions[opt.id || opt.name]);
    onAddToCart({
      menuId: menu._id || menu.id,
      name: menu.name,
      price: menu.price + optionsExtra,
      quantity,
      options: selectedList,
      imageUrl: menu.imageUrl || menu.image_url,
    });
    onClose();
  };

  return (
    <>
      <div className="overlay animate-fade-in" onClick={onClose} />
      <div className="menu-modal">
        <div className="menu-modal-inner">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '14px 16px 0',
        }}>
          <button className="sheet-close-btn" style={{ position: 'static' }} onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div style={{ height: 200, overflow: 'hidden', position: 'relative', background: 'var(--border)' }}>
          {menu.imageUrl || menu.image_url ? (
            <img
              src={menu.imageUrl || menu.image_url}
              alt={menu.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div
              style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--border)' }}
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-quaternary)" strokeWidth="1.5">
                <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
                <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
                <line x1="6" y1="1" x2="6" y2="4"/>
                <line x1="10" y1="1" x2="10" y2="4"/>
                <line x1="14" y1="1" x2="14" y2="4"/>
              </svg>
            </div>
          )}
          {isSoldOut && (
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                color: 'var(--text-tertiary)',
                fontSize: 16,
                fontWeight: 700,
                background: 'rgba(255,255,255,0.9)',
                padding: '6px 16px',
                borderRadius: 'var(--radius-full)',
              }}>품절</span>
            </div>
          )}
        </div>

        <div style={{ padding: '20px 20px 12px' }}>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 6, color: 'var(--text)', letterSpacing: '-0.5px' }}>
              {menu.name}
            </h2>
            {menu.description && (
              <p style={{ fontSize: 14, color: 'var(--text-tertiary)', lineHeight: 1.6, marginBottom: 8 }}>
                {menu.description}
              </p>
            )}
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
              {menu.price.toLocaleString()}원
            </div>
          </div>

          {options.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10 }}>
                추가 옵션
              </div>
              {options.map((opt) => {
                const key = opt.id || opt.name;
                const isSelected = !!selectedOptions[key];
                return (
                  <div
                    key={key}
                    className={`option-toggle ${isSelected ? 'selected' : ''}`}
                    onClick={() => !isSoldOut && toggleOption(opt)}
                  >
                    <div className="option-check">
                      {isSelected && (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </div>
                    <span style={{ flex: 1, fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>{opt.name}</span>
                    {(opt.price_delta || 0) > 0 && (
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)' }}>
                        +{(opt.price_delta).toLocaleString()}원
                      </span>
                    )}
                    {(opt.price_delta || 0) === 0 && (
                      <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>무료</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="divider" />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div className="qty-stepper">
              <button className="qty-btn" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={isSoldOut}>-</button>
              <span className="qty-value">{quantity}</span>
              <button className="qty-btn" onClick={() => setQuantity(quantity + 1)} disabled={isSoldOut}>+</button>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 2 }}>합계</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px' }}>
                {totalPrice.toLocaleString()}원
              </div>
            </div>
          </div>

          <button
            className="btn btn-primary btn-block btn-lg"
            onClick={handleAdd}
            disabled={isSoldOut}
          >
            {isSoldOut ? '품절된 메뉴입니다' : '장바구니 담기'}
          </button>
        </div>
        </div>
      </div>
    </>
  );
}

export default function MenuPage() {
  const { storeId, tableId } = useParams();
  const { addItem, getItemCount, getTotal, seniorMode, sessionId, setPaymentMode } = useCart();
  const navigate = useNavigate();

  const [menus, setMenus] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeName, setStoreName] = useState('');
  const [tableNumber, setTableNumber] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedMenu, setSelectedMenu] = useState(null);

  const { toast, showToast } = useToast();

  const [staffCallOpen, setStaffCallOpen] = useState(false);
  const [staffCallCooldown, setStaffCallCooldown] = useState(false);
  const cooldownTimer = useRef(null);

  const [isClosed, setIsClosed] = useState(false);
  const [operatingHoursText, setOperatingHoursText] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const fetchMenus = useCallback(async () => {
    try {
      const data = await api.get(`/stores/${storeId}/menus`);
      const result = data?.data || data;
      const list = result?.menus || (Array.isArray(result) ? result : []);
      const cats = result?.categories || [];

      if (list.length > 0) {
        setMenus(list);
      }
      if (cats.length > 0) {
        setApiCategories(cats.map((c) => ({ id: c.id, name: c.name })));
      }
      if (result?.store?.name || result?.storeName) {
        setStoreName(result.store?.name || result.storeName);
      }
      if (result?.store?.payment_mode) {
        setPaymentMode(result.store.payment_mode);
      }
    } catch {}
    finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  useEffect(() => {
    if (!storeId || !tableId) return;
    api.get(`/stores/${storeId}/tables/${tableId}/qr`)
      .then((res) => {
        const r = res?.data || res;
        const num = r?.table_number ?? r?.data?.table_number;
        if (num != null) setTableNumber(num);
      })
      .catch(() => {});
  }, [storeId, tableId]);

  useEffect(() => {
    if (!storeId) return;
    api.get(`/stores/${storeId}/hours`)
      .then((res) => {
        const result = res?.data || res;
        if (!result) return;

        const hours = result.operating_hours || result;
        if (!hours || !hours.open || !hours.close) return;

        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        const [openH, openM] = hours.open.split(':').map(Number);
        const [closeH, closeM] = hours.close.split(':').map(Number);
        const openMinutes = openH * 60 + (openM || 0);
        const closeMinutes = closeH * 60 + (closeM || 0);

        const isOpen = closeMinutes > openMinutes
          ? (currentMinutes >= openMinutes && currentMinutes < closeMinutes)
          : (currentMinutes >= openMinutes || currentMinutes < closeMinutes);

        if (!isOpen) {
          setIsClosed(true);
          setOperatingHoursText(`${hours.open} ~ ${hours.close}`);
        }
      })
      .catch(() => {});
  }, [storeId]);

  useEffect(() => {
    if (!storeId) return;
    let socket;
    try {
      socket = getSocket(storeId);
      socket.on('menu_update', () => fetchMenus());
    } catch {}
    return () => {
      if (socket) socket.off('menu_update');
    };
  }, [storeId, fetchMenus]);

  useEffect(() => {
    return () => clearTimeout(cooldownTimer.current);
  }, []);

  // 오프라인 감지
  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // API 전역 에러 이벤트 처리
  useEffect(() => {
    const onSessionExpired = () => showToast('세션이 만료되었습니다. 다시 스캔해주세요.', 'error');
    const onServerError = () => showToast('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.', 'error');
    window.addEventListener('api:session-expired', onSessionExpired);
    window.addEventListener('api:server-error', onServerError);
    return () => {
      window.removeEventListener('api:session-expired', onSessionExpired);
      window.removeEventListener('api:server-error', onServerError);
    };
  }, [showToast]);

  const filteredMenus = menus.filter((m) => {
    const matchesCategory = activeCategory === 'all' || m.category_id === activeCategory;
    const matchesSearch = !searchQuery ||
      m.name.includes(searchQuery) ||
      (m.description && m.description.includes(searchQuery));
    return matchesCategory && matchesSearch;
  });

  const popularMenus = menus.filter(
    (m) => (m.isPopular || m.is_popular === 1 || m.is_popular === true) &&
           !(m.soldOut || m.isSoldOut || m.is_sold_out === 1 || m.is_sold_out === true || m.status === 'soldout')
  );

  const handleAddToCart = (item) => {
    if (isClosed) return;
    const isSoldOut = item.soldOut || item.isSoldOut || item.is_sold_out === 1 || item.is_sold_out === true || item.status === 'soldout';
    if (isSoldOut) return;
    addItem({
      menuId: item._id || item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
      options: {},
      imageUrl: item.imageUrl || item.image_url,
    });
    showToast(`${item.name} 담았어요!`, 'success');
  };

  const handleStaffCall = async ({ type, message }) => {
    setStaffCallOpen(false);
    try {
      await api.post('/staff-calls', {
        store_id: storeId,
        table_id: tableId,
        table_number: tableNumber,
        type,
        message,
      });
      showToast('호출 완료', 'success');
      setStaffCallCooldown(true);
      cooldownTimer.current = setTimeout(() => {
        setStaffCallCooldown(false);
      }, 30000);
    } catch {
      showToast('호출에 실패했습니다', 'error');
    }
  };

  const cartCount = getItemCount();
  const cartTotal = getTotal();

  return (
    <>
    <div className="page" style={{ paddingBottom: cartCount > 0 ? 'calc(60px + 72px + var(--safe-bottom))' : 'calc(60px + var(--safe-bottom))' }}>
      {/* Operating hours closed overlay */}
      {isClosed && (
        <div
          className="animate-fade-in"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.95)',
            zIndex: 500,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center',
            gap: 12,
          }}
        >
          <div style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px' }}>
            현재 영업시간이 아닙니다
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
            영업시간: {operatingHoursText}
          </div>
        </div>
      )}

      {/* 오프라인 배너 */}
      {isOffline && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          background: '#f5c518',
          color: '#1a1a1a',
          textAlign: 'center',
          padding: '10px 16px',
          fontSize: 13,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="1" y1="1" x2="23" y2="23"/>
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
            <path d="M10.71 5.05A16 16 0 0 1 22.56 9"/>
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
            <line x1="12" y1="20" x2="12.01" y2="20"/>
          </svg>
          인터넷 연결을 확인해주세요
        </div>
      )}

      {/* Sticky header + category tabs */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100 }}>
        <header className="store-header">
          <div className="store-header-inner">
            <div>
              <div className="store-header-table">
                테이블 {tableNumber != null ? tableNumber : ''}번
              </div>
              <div className="store-header-name">{storeName || '스마트 오더'}</div>
            </div>
            <SeniorModeToggle />
          </div>
        </header>

        {/* 검색 인풋 */}
        <div style={{
          padding: '8px 16px',
          background: 'var(--bg)',
          borderBottom: searchQuery ? '1px solid var(--border)' : 'none',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--bg-secondary)',
            borderRadius: 12,
            padding: '0 12px',
            height: 40,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="메뉴 검색"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 14,
                color: 'var(--text)',
                caretColor: 'var(--primary)',
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: 'var(--text-quaternary)',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* 검색 중에는 카테고리 탭 숨김 */}
        {!searchQuery && (
          <CategoryTabs
            categories={apiCategories}
            activeCategory={activeCategory}
            onSelect={setActiveCategory}
          />
        )}
      </div>

      {/* Menu list */}
      {loading ? (
        <div style={{ padding: '20px' }}>
          {[1,2,3,4].map(i => (
            <div key={i} className="skeleton" style={{ height: 88, marginBottom: 12, borderRadius: 16 }} />
          ))}
        </div>
      ) : (
        <>
          {/* Recommended (popular) section */}
          {popularMenus.length > 0 && activeCategory === 'all' && (
            <div style={{ padding: '20px 0 0' }}>
              <div style={{ padding: '0 20px', marginBottom: 12 }}>
                <div className="section-title" style={{ marginBottom: 0 }}>추천 메뉴</div>
              </div>
              <div
                className="recommended-scroll"
                style={{
                  display: 'flex',
                  gap: 12,
                  overflowX: 'auto',
                  padding: '0 20px 16px',
                  scrollbarWidth: 'none',
                }}
              >
                {popularMenus.map((menu) => (
                  <div
                    key={menu._id || menu.id}
                    onClick={() => !isClosed && setSelectedMenu(menu)}
                    style={{
                      flexShrink: 0,
                      width: 140,
                      background: 'var(--surface)',
                      borderRadius: 'var(--radius-lg)',
                      overflow: 'hidden',
                      boxShadow: 'var(--shadow-sm)',
                      cursor: isClosed ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                        background: 'var(--border)',
                      }}
                    >
                      {(menu.imageUrl || menu.image_url) ? (
                        <img
                          src={menu.imageUrl || menu.image_url}
                          alt={menu.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-quaternary)" strokeWidth="1.5">
                          <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
                          <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
                        </svg>
                      )}
                      <span
                        className="menu-badge menu-badge-popular"
                        style={{ position: 'absolute', top: 8, left: 8 }}
                      >
                        인기
                      </span>
                    </div>
                    <div style={{ padding: '10px 12px 12px' }}>
                      <div style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'var(--text)',
                        marginBottom: 4,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {menu.name}
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                        {(menu.price || 0).toLocaleString()}원
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ height: 8, background: 'var(--border)' }} />
            </div>
          )}

          {/* Main menu list */}
          {filteredMenus.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                {searchQuery ? (
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                ) : (
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
                    <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
                    <line x1="6" y1="1" x2="6" y2="4"/>
                    <line x1="10" y1="1" x2="10" y2="4"/>
                    <line x1="14" y1="1" x2="14" y2="4"/>
                  </svg>
                )}
              </div>
              <div className="empty-state-title">
                {searchQuery ? `'${searchQuery}'에 대한 메뉴가 없습니다` : '메뉴가 없습니다'}
              </div>
              <div className="empty-state-text">
                {searchQuery ? '다른 검색어를 입력해보세요' : '다른 카테고리를 선택해보세요'}
              </div>
            </div>
          ) : (
            <div className="menu-list">
              {filteredMenus.map((menu, idx) => (
                <div key={menu._id || menu.id || idx}>
                  <MenuCard
                    menu={menu}
                    index={idx}
                    onSelect={(m) => !isClosed && setSelectedMenu(m)}
                    onAdd={handleAddToCart}
                    seniorMode={seniorMode}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Cart floating bar */}
      {cartCount > 0 && (
        <div className="cart-bar">
          <button
            className="cart-bar-btn"
            onClick={() => navigate(`/cart/${storeId}/${tableId}`)}
          >
            <span className="cart-bar-count">{cartCount}</span>
            <span className="cart-bar-label">장바구니 보기</span>
            <span className="cart-bar-total">{cartTotal.toLocaleString()}원</span>
          </button>
        </div>
      )}

      </div>

      {/* Fixed elements outside .page to avoid transform context issues */}

      {/* Staff call FAB */}
      <button
        onClick={() => !staffCallCooldown && setStaffCallOpen(true)}
        disabled={staffCallCooldown}
        style={{
          position: 'fixed',
          bottom: cartCount > 0 ? 'calc(60px + 72px + var(--safe-bottom) + 16px)' : 'calc(60px + var(--safe-bottom) + 16px)',
          right: 16,
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: staffCallCooldown ? 'var(--text-quaternary)' : 'var(--primary)',
          color: '#fff',
          border: 'none',
          cursor: staffCallCooldown ? 'not-allowed' : 'pointer',
          boxShadow: staffCallCooldown ? 'none' : '0 4px 16px rgba(191,155,48,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 180,
          transition: 'all 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
          opacity: staffCallCooldown ? 0.5 : 1,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
      </button>

      {/* Staff call bottom sheet */}
      <StaffCallSheet
        open={staffCallOpen}
        onClose={() => setStaffCallOpen(false)}
        onSubmit={handleStaffCall}
      />

      {/* Bottom navigation */}
      <BottomNav />

      {/* Menu detail sheet */}
      {selectedMenu && (
        <MenuDetailSheet
          menu={selectedMenu}
          onClose={() => setSelectedMenu(null)}
          onAddToCart={(item) => {
            addItem(item);
            showToast(`${item.name} 담았어요!`, 'success');
          }}
        />
      )}

      {/* Toast */}
      <Toast {...toast} />
    </>
  );
}
